import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { Target, Layers, MapPin } from 'lucide-react';
import { startOfWeek } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

import { MassnahmeDialog, ImportDialog, MassnahmenListTab } from './shifts';
import { MasterMeasuresCard } from './shifts/MasterMeasuresCard';
import MassnahmenScheduleDialog from './shifts/MassnahmenScheduleDialog';
import { DeleteMassnahmeDialog } from './shifts/DeleteMassnahmeDialog';
import { useShiftsData } from './shifts/useShiftsData';
import { useShiftsActions } from './shifts/useShiftsActions';
import { massnahmenAPI } from '../lib/api';

export default function Massnahmen() {
  const { isAdmin, hasPermission } = useAuth();
  const canManage = isAdmin || hasPermission('shifts', 'edit');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('massnahmen');
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [massnahmenSortField, setMassnahmenSortField] = useState('name');
  const [massnahmenSortDirection, setMassnahmenSortDirection] = useState('asc');
  const [shiftCounts, setShiftCounts] = useState({});

  // Schlanke Gesamt-Einsatzzähler je Maßnahme (eine Aggregation) — statt die
  // komplette Woche an Einsätzen zu laden. Hält die Seite schnell.
  useEffect(() => {
    massnahmenAPI.shiftCounts()
      .then((r) => setShiftCounts(r.data || {}))
      .catch(() => setShiftCounts({}));
  }, []);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const data = useShiftsData({ weekStart, selectedLocationId, viewMode: 'week', monthDate: new Date(), autoLoadShifts: false });
  const actions = useShiftsActions({
    shifts: data.shifts,
    massnahmen: data.massnahmen,
    setMassnahmen: data.setMassnahmen,
    selectedLocationId,
    loadShifts: data.loadShifts,
    loadMassnahmen: data.loadMassnahmen,
    loadBaseData: data.loadBaseData,
    shiftCounts,
  });

  const handleMassnahmenSort = (field) => {
    if (massnahmenSortField === field) {
      setMassnahmenSortDirection((p) => (p === 'asc' ? 'desc' : 'asc'));
    } else {
      setMassnahmenSortField(field);
      setMassnahmenSortDirection('asc');
    }
  };

  const sortedMassnahmen = [...data.massnahmen].sort((a, b) => {
    const direction = massnahmenSortDirection === 'asc' ? 1 : -1;
    switch (massnahmenSortField) {
      case 'name':
        return direction * (a.name || '').localeCompare(b.name || '');
      case 'short_code':
        return direction * (a.short_code || '').localeCompare(b.short_code || '');
      case 'laufzeit': {
        const aStart = a.start_date || '', bStart = b.start_date || '';
        if (aStart !== bStart) return direction * aStart.localeCompare(bStart);
        return direction * ((a.end_date || '').localeCompare(b.end_date || ''));
      }
      case 'teilnehmer':
        return direction * ((parseInt(a.teilnehmer_anzahl) || 0) - (parseInt(b.teilnehmer_anzahl) || 0));
      case 'uhrzeit': {
        const firstTime = (m) => {
          const wt = m.weekday_times;
          if (wt && typeof wt === 'object') {
            for (const k of ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']) {
              if (wt[k] && wt[k].start) return wt[k].start;
            }
          }
          return m.start_time || '';
        };
        const at = firstTime(a), bt = firstTime(b);
        if (!at && bt) return 1;
        if (at && !bt) return -1;
        return direction * at.localeCompare(bt);
      }
      default:
        return 0;
    }
  });

  return (
    <DashboardLayout wide>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Maßnahmen</h1>
            <p className="text-muted-foreground">Maßnahmen und übergeordnete Maßnahmen (Vergaben) verwalten</p>
          </div>
        </div>

        {/* Standort-Auswahl: jetzt im Tab-Inhalt, damit die Reiterzeile
            unabhängig vom aktiven Reiter immer an gleicher Stelle steht. */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="massnahmen" data-testid="tab-massnahmen"><Target className="h-4 w-4 mr-2" />Maßnahmen verwalten</TabsTrigger>
            {canManage && (
              <TabsTrigger value="master-measures" data-testid="tab-master-measures"><Layers className="h-4 w-4 mr-2" />Übergeordnete Maßnahmen</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="massnahmen" className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Standort:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedLocationId === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedLocationId('all')}
                      data-testid="massnahmen-location-all"
                    >Alle Standorte</Button>
                    {data.locations.map((loc) => (
                      <Button
                        key={loc.id}
                        variant={selectedLocationId === loc.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedLocationId(loc.id)}
                        data-testid={`massnahmen-location-${loc.id}`}
                      >{loc.name}</Button>
                    ))}
                  </div>
                  {selectedLocationId && selectedLocationId !== 'all' && data.locations.find((l) => l.id === selectedLocationId)?.short_id && (
                    <Badge variant="outline" className="font-mono text-xs ml-auto">
                      ID: {data.locations.find((l) => l.id === selectedLocationId)?.short_id}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            <MassnahmenListTab
              selectedLocationId={selectedLocationId}
              locations={data.locations}
              massnahmen={data.massnahmen}
              shiftCounts={shiftCounts}
              onShowShifts={(m) => navigate(`/shifts?massnahme=${m.id}`)}
              sortedMassnahmen={sortedMassnahmen}
              massnahmenSortField={massnahmenSortField}
              massnahmenSortDirection={massnahmenSortDirection}
              handleMassnahmenSort={handleMassnahmenSort}
              canManage={canManage}
              openNewMassnahmeDialog={actions.openNewMassnahmeDialog}
              openEditMassnahmeDialog={actions.openEditMassnahmeDialog}
              handleDeleteMassnahme={actions.handleDeleteMassnahme}
              handleDownloadTemplate={actions.handleDownloadTemplate}
              setImportDialogOpen={actions.setImportDialogOpen}
              onOpenScheduleEditor={() => setScheduleDialogOpen(true)}
              onReload={data.loadMassnahmen}
            />
          </TabsContent>

          {canManage && (
            <TabsContent value="master-measures" className="space-y-4">
              <MasterMeasuresCard />
            </TabsContent>
          )}
        </Tabs>

        <MassnahmeDialog
          open={actions.massnahmeDialogOpen}
          onOpenChange={actions.setMassnahmeDialogOpen}
          selectedMassnahme={actions.selectedMassnahme}
          formData={actions.massnahmeForm}
          setFormData={actions.setMassnahmeForm}
          locations={data.locations}
          saving={actions.saving}
          onSave={actions.handleSaveMassnahme}
        />

        <ImportDialog
          open={actions.importDialogOpen}
          onOpenChange={actions.setImportDialogOpen}
          locations={data.locations}
          importing={actions.importing}
          onImport={actions.handleImport}
          onDownloadTemplate={actions.handleDownloadTemplate}
        />

        <MassnahmenScheduleDialog
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          massnahmen={sortedMassnahmen}
          canManage={canManage}
          onChange={() => {
            data.loadMassnahmen?.();
            data.loadShifts?.();
          }}
        />

        <DeleteMassnahmeDialog
          state={actions.deleteMassnahmeDialog}
          onConfirm={actions.confirmDeleteMassnahme}
          onCancel={actions.closeDeleteMassnahmeDialog}
        />
      </div>
    </DashboardLayout>
  );
}
